import React from "react";
import { BoltIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import { formatSeconds, parseTimeToSeconds } from "utils/timeUtils";

interface EstimatedStartTimeBadgeProps {
  estimatedStartTime: string;
}

/**
 * Categorizes start time as fast, average, or slow
 * Fast: < 5 minutes (300 seconds)
 * Average: 5-10 minutes (300-600 seconds)
 * Slow: > 10 minutes (600+ seconds)
 *
 * @param seconds - The time in seconds to categorize
 * @returns The category: "fast", "average", or "slow"
 *
 * @example
 * getStartTimeCategory(60) // returns "fast"
 * getStartTimeCategory(400) // returns "average"
 * getStartTimeCategory(700) // returns "slow"
 */
export const getStartTimeCategory = (
  seconds: number
): "fast" | "average" | "slow" => {
  if (seconds < 5 * 60) return "fast";
  if (seconds < 10 * 60) return "average";
  return "slow";
};

const EstimatedStartTimeBadge: React.FC<EstimatedStartTimeBadgeProps> = ({
  estimatedStartTime,
}) => {
  const seconds = parseTimeToSeconds(estimatedStartTime);

  // If no valid time provided, don't render anything
  if (seconds === undefined) {
    return null;
  }

  const category = getStartTimeCategory(seconds);

  const labelConfig = {
    fast: {
      color: "text-green-500" as const,
      lightningBolt: true,
    },
    average: {
      color: "text-yellow-500" as const,
      lightningBolt: false,
    },
    slow: {
      color: "text-orange-500" as const,
      lightningBolt: false,
    },
  };

  const { color, lightningBolt } = labelConfig[category];

  return (
    <div className="flex flex-row items-center text-secondary text-sm font-medium gap-2">
      {category === "fast" && (
        <div
          className={classNames("w-3 h-3 animate-pulse", color)}
          style={{
            filter: `drop-shadow(0 0 3px green)`,
            animationDuration: "1s",
          }}
        >
          <BoltIcon className="w-3 h-3" />
        </div>
      )}
      Ready in <span className={color}>{formatSeconds(seconds)}</span>
    </div>
  );
};

export default EstimatedStartTimeBadge;
